import {ScrollViewProvider} from 'components/Context/ScrollView';
import lodash from 'lodash';
import {FC, useRef} from 'react';
import {twMerge} from 'tailwind-merge';

import {useCachedPDFContext} from '../context';
import {useLazyLoadedImage, useReadyToRender} from '../hooks';
import {ErrorImage, PlaceholderImage} from '../Placeholder';
import {buildCachedPDFUrl} from '../utils';

const PageLoader: FC<{
    isActive: boolean;
    page: number;
    src: string;
}> = ({isActive, page, src}) => {
    const imageReference = useRef<HTMLImageElement>(null);
    const url = buildCachedPDFUrl(src, page, 300);
    const shouldRender = useReadyToRender(imageReference, {key: url, page});
    const {data, error, isLoading} = useLazyLoadedImage(url, shouldRender);
    if (isLoading)
        return (
            <PlaceholderImage
                className="flex w-36 justify-center"
                ref={imageReference}
            />
        );
    if (error)
        return (
            <ErrorImage
                className="flex w-36 justify-center"
                ref={imageReference}
            />
        );
    return (
        <img
            alt={`Page preview ${page}`}
            className={twMerge(
                'w-36 rounded border border-grey-300 opacity-50 hover:opacity-100',
                isActive &&
                    'opacity-60 hover:opacity-100 outline outline-green-500 outline-4',
            )}
            ref={imageReference}
            src={data}
        />
    );
};
const PagePreviews: FC<{
    src: string;
}> = ({src}) => {
    const context = useCachedPDFContext();

    if (!context.showPagePreviews) return null;

    return (
        <ScrollViewProvider className="absolute inset-y-0 left-0 flex flex-col items-center gap-4 overflow-y-auto border-r border-r-grey-300 bg-grey-200 px-4 py-8">
            {lodash.range(1, context.maxPages + 1).map((page) => {
                const pageUrl = buildCachedPDFUrl(src, page, 120);
                const isActive = context.currentPage === page;
                return (
                    <button
                        aria-label={`Page preview button ${page}`}
                        key={`page-preview-${page}`}
                        onClick={() => context.gotoPage(page)}
                    >
                        <PageLoader
                            isActive={isActive}
                            page={page}
                            src={pageUrl}
                        />
                    </button>
                );
            })}
        </ScrollViewProvider>
    );
};

export default PagePreviews;
